import { differenceInCalendarDays, parseISO } from 'date-fns';
import { AMOUNT_FORMAT, toExcelDate } from 'components/xlsx/excelCells';
import type ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import { OpenInvoiceRow } from 'types/openInvoices';
import { t } from 'utils/getTranslations';

export type UnpaidInvoicesView = 'ACTIVE' | 'COLLECTION_PREVENTED';

/** A selected party-group, resolved to its member payer party ids (role MEMBER, not soft-expired). */
export interface UnpaidInvoicesGroupSection {
    groupName: string;
    memberPartyIds: Set<number>;
}

type Cell = string | number | null;

const COLLECTION_PREVENTED_RECEIVABILITY = ['WRITTEN_OFF', 'IRRECOVERABLE'];

/** 1-based column indices holding money amounts in the Active workbook (Net Transactions..Paid VAT). */
const ACTIVE_AMOUNT_COLUMN_INDICES = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];

/** 1-based column indices holding money amounts in the Collection Prevented workbook (Payable, Open). */
const COLLECTION_PREVENTED_AMOUNT_COLUMN_INDICES = [8, 9];

const round2 = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

const isCollectionPrevented = (row: OpenInvoiceRow): boolean =>
    COLLECTION_PREVENTED_RECEIVABILITY.includes(row.receivabilityTypeCode);

/**
 * Formats an ISO date (yyyy-MM-dd) as DD.MM.YYYY for display. Returns an empty string when absent.
 */
const formatDisplayDate = (isoDate: string | null | undefined): string => {
    if (!isoDate) {
        return '';
    }
    const [year, month, day] = isoDate.split('-');
    if (!year || !month || !day) {
        return isoDate;
    }
    return `${day}.${month}.${year}`;
};

/**
 * One row's derived report fields, all money values pre-rounded to 2 decimals so the exported cells
 * and the subtotals/grand total aggregate the exact same values (same discipline as
 * openInvoicesExcel.ts).
 */
interface DerivedRow {
    payerId: number;
    payer: string;
    owner: string;
    responsible: string;
    invoiceNumber: string;
    primaryMatter: string;
    entryDate: string;
    dueDate: string;
    debtNotes: string;
    overdueDays: number;
    totalInvoice: number;
    totalOpen: number;
    totalPaid: number;
    payable: number;
    netTrans: number;
    netExp: number;
    netDiscount: number;
    netInvoice: number;
    netPaid: number;
    netOpen: number;
    netOpenTrans: number;
    netOpenExp: number;
    paidVat: number;
}

const deriveRow = (row: OpenInvoiceRow, asOfDate: string): DerivedRow => {
    const totalInvoice = round2(row.invoicedBaseCurrency);
    const totalOpen = round2(row.openBaseCurrency);
    const totalPaid = round2(-(row.paidBaseCurrency + row.creditedBaseCurrency));
    const payable = round2(row.invoicedBaseCurrency - row.creditedBaseCurrency);
    const overdueDays = differenceInCalendarDays(parseISO(asOfDate), parseISO(row.dueDate));

    const invoiceTotals = row.baseCurrency?.invoice;
    const netInvoice = invoiceTotals?.totalWithoutVat ?? 0;
    const rate =
        invoiceTotals && invoiceTotals.totalWithoutVat !== 0 ? invoiceTotals.vat / invoiceTotals.totalWithoutVat : 0;
    // ponytail: 1 + rate === 0 only for a degenerate vat === -totalWithoutVat invoice; no proportional
    // split is meaningful there, so treat the payment as pure net rather than divide by zero.
    const netPaid = 1 + rate !== 0 ? totalPaid / (1 + rate) : totalPaid;
    const paidVat = totalPaid - netPaid;
    const netOpen = netInvoice + netPaid;
    const netTrans = row.baseCurrency?.fees.totalWithoutVat ?? 0;
    const netExp =
        (row.baseCurrency?.expensesWithoutVat.totalWithoutVat ?? 0) +
        (row.baseCurrency?.expensesWithVat.totalWithoutVat ?? 0);
    const netDiscount = netInvoice - netTrans - netExp;

    const baseFE = netTrans + netExp;
    const transBucket = baseFE !== 0 ? netTrans + netDiscount * (netTrans / baseFE) : netTrans + netDiscount;
    const ratio = netInvoice !== 0 ? netOpen / netInvoice : 0;
    const netOpenTrans = round2(transBucket * ratio);
    const netOpenExp = round2(netOpen - netOpenTrans);

    return {
        payerId: row.payerId,
        payer: row.payer,
        owner: row.owner,
        responsible: row.responsible,
        invoiceNumber: row.invoiceNumber,
        primaryMatter: row.primaryMatter,
        entryDate: row.entryDate,
        dueDate: row.dueDate,
        debtNotes: row.debtNotes,
        overdueDays,
        totalInvoice,
        totalOpen,
        totalPaid,
        payable,
        netTrans: round2(netTrans),
        netExp: round2(netExp),
        netDiscount: round2(netDiscount),
        netInvoice: round2(netInvoice),
        netPaid: round2(netPaid),
        netOpen: round2(netOpen),
        netOpenTrans,
        netOpenExp,
        paidVat: round2(paidVat),
    };
};

type ActiveTotals = {
    netTrans: number;
    netExp: number;
    netDiscount: number;
    netInvoice: number;
    netPaid: number;
    netOpen: number;
    netOpenTrans: number;
    netOpenExp: number;
    totalInvoice: number;
    totalOpen: number;
    totalPaid: number;
    paidVat: number;
};

const emptyActiveTotals = (): ActiveTotals => ({
    netTrans: 0,
    netExp: 0,
    netDiscount: 0,
    netInvoice: 0,
    netPaid: 0,
    netOpen: 0,
    netOpenTrans: 0,
    netOpenExp: 0,
    totalInvoice: 0,
    totalOpen: 0,
    totalPaid: 0,
    paidVat: 0,
});

const addToActiveTotals = (totals: ActiveTotals, row: DerivedRow): void => {
    totals.netTrans += row.netTrans;
    totals.netExp += row.netExp;
    totals.netDiscount += row.netDiscount;
    totals.netInvoice += row.netInvoice;
    totals.netPaid += row.netPaid;
    totals.netOpen += row.netOpen;
    totals.netOpenTrans += row.netOpenTrans;
    totals.netOpenExp += row.netOpenExp;
    totals.totalInvoice += row.totalInvoice;
    totals.totalOpen += row.totalOpen;
    totals.totalPaid += row.totalPaid;
    totals.paidVat += row.paidVat;
};

const mergeActiveTotals = (target: ActiveTotals, source: ActiveTotals): void => {
    target.netTrans += source.netTrans;
    target.netExp += source.netExp;
    target.netDiscount += source.netDiscount;
    target.netInvoice += source.netInvoice;
    target.netPaid += source.netPaid;
    target.netOpen += source.netOpen;
    target.netOpenTrans += source.netOpenTrans;
    target.netOpenExp += source.netOpenExp;
    target.totalInvoice += source.totalInvoice;
    target.totalOpen += source.totalOpen;
    target.totalPaid += source.totalPaid;
    target.paidVat += source.paidVat;
};

const activeTotalsCells = (totals: ActiveTotals): Cell[] => [
    totals.netTrans,
    totals.netExp,
    totals.netDiscount,
    totals.netInvoice,
    totals.netPaid,
    totals.netOpen,
    totals.netOpenTrans,
    totals.netOpenExp,
    totals.totalInvoice,
    totals.totalOpen,
    totals.totalPaid,
    totals.paidVat,
];

/** Applies the amount number format to the given amount columns of a worksheet row. */
const formatAmountCells = (row: ExcelJS.Row, columnIndices: number[]): void => {
    columnIndices.forEach((columnIndex) => {
        row.getCell(columnIndex).numFmt = AMOUNT_FORMAT;
    });
};

const groupByOwner = (rows: DerivedRow[]): Map<string, DerivedRow[]> => {
    const groups = new Map<string, DerivedRow[]>();
    rows.forEach((row) => {
        const existing = groups.get(row.owner);
        if (existing) {
            existing.push(row);
        } else {
            groups.set(row.owner, [row]);
        }
    });
    return groups;
};

const sortByPayerThenInvoiceNumber = (rows: DerivedRow[]): DerivedRow[] =>
    [...rows].sort((a, b) => a.payer.localeCompare(b.payer) || a.invoiceNumber.localeCompare(b.invoiceNumber));

const addActiveDataRow = (worksheet: ExcelJS.Worksheet, ownerLabel: string, row: DerivedRow): void => {
    const dataRow = worksheet.addRow([
        ownerLabel,
        row.responsible,
        row.invoiceNumber,
        row.payer,
        row.primaryMatter,
        toExcelDate(row.entryDate),
        toExcelDate(row.dueDate),
        row.netTrans,
        row.netExp,
        row.netDiscount,
        row.netInvoice,
        row.netPaid,
        row.netOpen,
        row.netOpenTrans,
        row.netOpenExp,
        row.totalInvoice,
        row.totalOpen,
        row.totalPaid,
        row.paidVat,
        row.overdueDays,
        row.debtNotes,
        null,
    ]);
    formatAmountCells(dataRow, ACTIVE_AMOUNT_COLUMN_INDICES);
};

const addActiveTotalRow = (worksheet: ExcelJS.Worksheet, label: string, totals: ActiveTotals): void => {
    const totalRow = worksheet.addRow([label, '', '', '', '', '', '', ...activeTotalsCells(totals), '', '']);
    totalRow.font = { bold: true };
    formatAmountCells(totalRow, ACTIVE_AMOUNT_COLUMN_INDICES);
};

/** Renders the per-owner groups, each with its own bold owner-total subtotal row. */
const renderOwnerGroups = (worksheet: ExcelJS.Worksheet, rows: DerivedRow[]): ActiveTotals => {
    const sectionTotal = emptyActiveTotals();

    const groups = groupByOwner(rows);
    [...groups.keys()]
        .sort((a, b) => a.localeCompare(b))
        .forEach((owner) => {
            const ownerRows = sortByPayerThenInvoiceNumber(groups.get(owner) ?? []);
            const ownerTotal = emptyActiveTotals();
            ownerRows.forEach((row, index) => {
                addToActiveTotals(ownerTotal, row);
                addToActiveTotals(sectionTotal, row);
                addActiveDataRow(worksheet, index === 0 ? owner : '', row);
            });
            addActiveTotalRow(worksheet, t('unpaid-invoices.owner-total', { owner }), ownerTotal);
        });

    return sectionTotal;
};

/** Renders one selected group's block: header, flat member rows (sorted), then a bold subtotal row. */
const renderGroupSection = (
    worksheet: ExcelJS.Worksheet,
    section: UnpaidInvoicesGroupSection,
    rows: DerivedRow[]
): ActiveTotals => {
    const groupTotal = emptyActiveTotals();

    const headerRow = worksheet.addRow([t('unpaid-invoices.group-header', { group: section.groupName })]);
    headerRow.font = { bold: true };

    sortByPayerThenInvoiceNumber(rows).forEach((row) => {
        addToActiveTotals(groupTotal, row);
        addActiveDataRow(worksheet, row.owner, row);
    });

    addActiveTotalRow(worksheet, t('unpaid-invoices.group-total', { group: section.groupName }), groupTotal);

    return groupTotal;
};

const buildActiveWorkbook = (
    workbook: ExcelJS.Workbook,
    rows: DerivedRow[],
    asOfDate: string,
    groupSections: UnpaidInvoicesGroupSection[]
): void => {
    const worksheet = workbook.addWorksheet(t('unpaid-invoices.sheet-name'));

    worksheet.columns = [
        { width: 24 }, // Matter Owner
        { width: 24 }, // Matter Responsible
        { width: 14 }, // Invoice Number
        { width: 28 }, // Payer
        { width: 20 }, // Matter
        { width: 12 }, // Date
        { width: 12 }, // Due Date
        { width: 14 }, // Net Transactions
        { width: 14 }, // Net Expenses
        { width: 14 }, // Net Discount
        { width: 14 }, // Net Invoice
        { width: 14 }, // Net Paid
        { width: 14 }, // Net Open
        { width: 16 }, // Net Open Transactions
        { width: 16 }, // Net Open Expenses
        { width: 14 }, // Total Invoice
        { width: 14 }, // Total Open
        { width: 14 }, // Total Paid
        { width: 14 }, // Paid VAT
        { width: 14 }, // Overdue (Days)
        { width: 28 }, // Comments
        { width: 8 }, // trailing tag column, left blank
    ];

    worksheet.addRow([t('unpaid-invoices.report-title')]);
    worksheet.addRow([`${t('unpaid-invoices.as-of')}: ${formatDisplayDate(asOfDate)}`]);
    worksheet.addRow([]);
    const headerRow = worksheet.addRow([
        t('unpaid-invoices.column.owner'),
        t('unpaid-invoices.column.responsible'),
        t('unpaid-invoices.column.invoice-number'),
        t('unpaid-invoices.column.payer'),
        t('unpaid-invoices.column.matter'),
        t('unpaid-invoices.column.date'),
        t('unpaid-invoices.column.due-date'),
        t('unpaid-invoices.column.net-transactions'),
        t('unpaid-invoices.column.net-expenses'),
        t('unpaid-invoices.column.net-discount'),
        t('unpaid-invoices.column.net-invoice'),
        t('unpaid-invoices.column.net-paid'),
        t('unpaid-invoices.column.net-open'),
        t('unpaid-invoices.column.net-open-transactions'),
        t('unpaid-invoices.column.net-open-expenses'),
        t('unpaid-invoices.column.total-invoice'),
        t('unpaid-invoices.column.total-open'),
        t('unpaid-invoices.column.total-paid'),
        t('unpaid-invoices.column.paid-vat'),
        t('unpaid-invoices.column.overdue-days'),
        t('unpaid-invoices.column.comments'),
        '',
    ]);
    headerRow.font = { bold: true };

    // Group membership is disjoint (per invoice): claim group rows first, everything left is per-owner.
    const claimedPayerIds = new Set<number>();
    const groupRowSets = groupSections.map((section) => {
        const memberRows = rows.filter(
            (row) => !claimedPayerIds.has(row.payerId) && section.memberPartyIds.has(row.payerId)
        );
        memberRows.forEach((row) => claimedPayerIds.add(row.payerId));
        return memberRows;
    });
    const ownerRows = rows.filter((row) => !claimedPayerIds.has(row.payerId));

    const grandTotal = emptyActiveTotals();
    mergeActiveTotals(grandTotal, renderOwnerGroups(worksheet, ownerRows));
    groupSections.forEach((section, index) => {
        mergeActiveTotals(grandTotal, renderGroupSection(worksheet, section, groupRowSets[index]));
    });

    addActiveTotalRow(worksheet, t('unpaid-invoices.grand-total'), grandTotal);
};

type FlatTotals = { payable: number; totalOpen: number };

const buildCollectionPreventedWorkbook = (workbook: ExcelJS.Workbook, rows: DerivedRow[], asOfDate: string): void => {
    const worksheet = workbook.addWorksheet(t('unpaid-invoices.sheet-name-collection-prevented'));

    worksheet.columns = [
        { width: 24 }, // Matter Owner
        { width: 24 }, // Matter Responsible
        { width: 14 }, // Invoice Number
        { width: 28 }, // Payer
        { width: 20 }, // Matter
        { width: 12 }, // Date
        { width: 12 }, // Due Date
        { width: 14 }, // Payable
        { width: 14 }, // Open
        { width: 14 }, // Overdue (Days)
        { width: 8 }, // trailing tag column, left blank
    ];

    worksheet.addRow([t('unpaid-invoices.report-title')]);
    worksheet.addRow([`${t('unpaid-invoices.as-of')}: ${formatDisplayDate(asOfDate)}`]);
    worksheet.addRow([]);
    const headerRow = worksheet.addRow([
        t('unpaid-invoices.column.owner'),
        t('unpaid-invoices.column.responsible'),
        t('unpaid-invoices.column.invoice-number'),
        t('unpaid-invoices.column.payer'),
        t('unpaid-invoices.column.matter'),
        t('unpaid-invoices.column.date'),
        t('unpaid-invoices.column.due-date'),
        t('unpaid-invoices.column.payable'),
        t('unpaid-invoices.column.open'),
        t('unpaid-invoices.column.overdue-days'),
        '',
    ]);
    headerRow.font = { bold: true };

    const grandTotal: FlatTotals = { payable: 0, totalOpen: 0 };
    sortByPayerThenInvoiceNumber(rows).forEach((row) => {
        grandTotal.payable += row.payable;
        grandTotal.totalOpen += row.totalOpen;
        const dataRow = worksheet.addRow([
            row.owner,
            row.responsible,
            row.invoiceNumber,
            row.payer,
            row.primaryMatter,
            toExcelDate(row.entryDate),
            toExcelDate(row.dueDate),
            row.payable,
            row.totalOpen,
            row.overdueDays,
            null,
        ]);
        formatAmountCells(dataRow, COLLECTION_PREVENTED_AMOUNT_COLUMN_INDICES);
    });

    const grandTotalRow = worksheet.addRow([
        t('unpaid-invoices.grand-total'),
        '',
        '',
        '',
        '',
        '',
        '',
        grandTotal.payable,
        grandTotal.totalOpen,
        '',
        '',
    ]);
    grandTotalRow.font = { bold: true };
    formatAmountCells(grandTotalRow, COLLECTION_PREVENTED_AMOUNT_COLUMN_INDICES);
};

/**
 * Builds the "Unpaid invoices" workbook (Active worklist or Collection prevented, per `view`) from the
 * flat per-invoice open-invoices rows and triggers a download. Rendered with exceljs so amount and date
 * columns are real number and date cells and header/subtotal/grand-total rows are bold.
 *
 * Group separation (`groupSections`) is a pure re-bucketing: selected-group payers move out of the
 * per-owner sections into their own `Group: …` block, but every row and the grand total stay identical
 * to the ungrouped layout.
 */
export const generateUnpaidInvoicesExcel = async (
    rows: OpenInvoiceRow[],
    asOfDate: string,
    view: UnpaidInvoicesView,
    overdueMoreThanDays: number | null,
    groupSections: UnpaidInvoicesGroupSection[]
): Promise<void> => {
    const ExcelJS = (await import('exceljs')).default;

    const viewRows = rows.filter((row) =>
        view === 'COLLECTION_PREVENTED' ? isCollectionPrevented(row) : !isCollectionPrevented(row)
    );
    const derivedRows = viewRows.map((row) => deriveRow(row, asOfDate));
    const filteredRows =
        view === 'ACTIVE' && overdueMoreThanDays !== null
            ? derivedRows.filter((row) => row.overdueDays > overdueMoreThanDays)
            : derivedRows;

    const workbook = new ExcelJS.Workbook();

    if (view === 'COLLECTION_PREVENTED') {
        buildCollectionPreventedWorkbook(workbook, filteredRows, asOfDate);
    } else {
        buildActiveWorkbook(workbook, filteredRows, asOfDate, groupSections);
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const filenameSuffix = view === 'COLLECTION_PREVENTED' ? '-collection-prevented' : '';
    saveAs(blob, `unpaid-invoices${filenameSuffix}-${asOfDate}.xlsx`);
};
